import { MouseEvent } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useSavedItems,
  type SavedItemInput,
  type SavedItemType,
} from "@/hooks/useSavedItems";

interface SaveButtonProps {
  item: SavedItemInput;
  variant?: "icon" | "pill";
  size?: "xs" | "sm";
  label?: string;
  className?: string;
}

const labelFor = (t: SavedItemType) => {
  switch (t) {
    case "article":
      return "article";
    case "curated_post":
      return "post";
    case "idea":
      return "idea";
    case "opportunity":
      return "opportunity";
    case "event":
      return "event";
  }
};

const SaveButton = ({
  item,
  variant = "pill",
  size = "sm",
  label,
  className,
}: SaveButtonProps) => {
  const { isSavedKey, toggle } = useSavedItems();
  const saved = isSavedKey(item.item_type, item.item_id);

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void toggle(item);
  };

  const ariaLabel = saved
    ? `Remove ${labelFor(item.item_type)} from your profile`
    : `Save ${labelFor(item.item_type)} to your profile`;

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-pressed={saved}
        title={saved ? "Saved to your profile" : "Save to your profile"}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card/80 backdrop-blur text-muted-foreground transition-colors hover:text-primary hover:border-primary/40",
          saved && "text-primary border-primary/40 bg-primary/10",
          className,
        )}
      >
        {saved ? (
          <BookmarkCheck className="h-4 w-4" />
        ) : (
          <Bookmark className="h-4 w-4" />
        )}
      </button>
    );
  }

  const heightClass = size === "xs" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-xs";

  return (
    <Button
      type="button"
      variant={saved ? "secondary" : "outline"}
      size="sm"
      className={cn(heightClass, saved && "text-primary", className)}
      onClick={onClick}
      aria-pressed={saved}
      aria-label={ariaLabel}
    >
      {saved ? (
        <BookmarkCheck className="h-3.5 w-3.5 mr-1.5" />
      ) : (
        <Bookmark className="h-3.5 w-3.5 mr-1.5" />
      )}
      {label ?? (saved ? "Saved" : "Save")}
    </Button>
  );
};

export default SaveButton;